import React, { useCallback, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { View, Keyboard, Alert } from 'react-native';
import emoji from 'node-emoji';
import { HeaderBackButton } from '@react-navigation/elements';
import Button from 'design-system/Button';
import useColors from 'hooks/useColors';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import { AppView, rem, Spacer } from 'design-system';
import StatusBar from 'design-system/StatusBar';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import KeyboardAvoidingView from 'design-system/KeyboardAvoidingView';
import TextInput from 'utils/packages/TextInput';
import { useMarginBottom } from 'hooks/useMarginBottom';
// like ChangeDetail, but universal
const TextFieldPage = ({ route, navigation, }) => {
    const colors = useColors();
    const title = route.params?.title;
    const onPressNext = route.params?.onPressNext;
    const placeholder = route.params?.placeholder;
    const onBackPress = route.params?.onBackPress;
    const defaultValue = route.params?.defaultValue;
    const textFieldProps = route.params?.textFieldProps;
    const autoCapitalize = route.params?.autoCapitalize;
    const buttonTitle = route.params?.buttonTitle;
    const styles = useStyles(styleSet);
    useLayoutEffect(() => {
        navigation.setOptions({
            title,
            headerTintColor: colors.text.primary,
            headerLeft: onBackPress
                ? () => (<HeaderBackButton displayMode="minimal" onPress={() => {
                        Keyboard.dismiss();
                        onBackPress?.(navigation);
                    }} style={styles.leftBtn} tintColor={colors.elements.primary}/>)
                : undefined,
        });
    }, [
        colors.elements.primary,
        colors.elements.white,
        colors.text.primary,
        onBackPress,
        styles.leftBtn,
        title,
        colors.text.white,
    ]);
    const [value, setValue] = useState(defaultValue || '');
    const [isFetching, setIsFetching] = useState(false);
    const inputRef = useRef(null);
    useAutoFocusInput(inputRef);
    const paddingBottom = useMarginBottom('paddingBottom');
    const handlePress = useCallback(() => {
        if (value?.length > 0) {
            Keyboard.dismiss();
            onPressNext?.(value, setIsFetching, inputRef);
        }
        else {
            Alert.alert(emoji.emojify('Hold on :dizzy:'), 'You didn’t write anything');
        }
    }, [onPressNext, value]);
    const textInput = useMemo(() => (<TextInput ref={inputRef} style={styles.textInput} value={value} placeholder={placeholder || ''} placeholderTextColor={colors.text.placeholder} selectionColor={colors.text.selection} underlineColorAndroid="transparent" textAlign="center" autoCorrect={false} allowFontScaling={false} autoCapitalize={autoCapitalize} onChangeText={setValue} onSubmitEditing={handlePress} focusStyle={styles.textInputFocus} {...textFieldProps}/>), [
        styles.textInput,
        styles.textInputFocus,
        value,
        placeholder,
        colors.text.placeholder,
        colors.text.selection,
        autoCapitalize,
        handlePress,
        textFieldProps,
    ]);
    const button = useMemo(() => (<View style={[styles.buttonContainer, paddingBottom]}>
        <Button brand disabled={!value?.length} isFetching={isFetching} onPress={handlePress} title={buttonTitle || 'Next'}/>
      </View>), [
        styles.buttonContainer,
        paddingBottom,
        value?.length,
        isFetching,
        handlePress,
        buttonTitle,
    ]);
    const renderContent = () => (<KeyboardAvoidingView behavior="padding" style={styles.container}>
      <Spacer h={72}/>
      {textInput}
      <Spacer flex/>
      {button}
    </KeyboardAvoidingView>);
    return (<AppView type="secondary" isInTab avoidHeader>
      <StatusBar barStyle="dark-content" animated/>
      {renderContent()}
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    content: {
        flex: 1,
    },
    container: {
        flex: 1,
        marginHorizontal: rem(16),
        justifyContent: 'center',
    },
    alignCenter: {
        alignItems: 'center',
    },
    textInput: {
        paddingVertical: rem(16),
        color: colors.text.secondary,
    },
    leftBtn: {
        marginLeft: 20,
    },
    textInputFocus: {
        color: colors.text.primary,
    },
    buttonContainer: {
        paddingHorizontal: 0,
    },
}));
export default TextFieldPage;
